import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useSession } from "../lib/session";
import { PageHeading, Alert, Spinner } from "../components/ui";

/** Staff and coordinators only -- App sends students and industry users to /positions. */
export default function Dashboard() {
  const { user } = useSession();
  if (!user) return null;

  if (user.role === "COORDINATOR") return <CoordinatorHome />;
  return <StaffHome />;
}

function StaffHome() {
  return (
    <>
      <PageHeading title="Referee dashboard" description="Students who have nominated you as a referee." />
      <RefereeSummary />
    </>
  );
}

/** Shared by staff and coordinators -- a coordinator is a staff member with extra powers. */
function RefereeSummary() {
  const { data, isLoading } = useQuery({
    queryKey: ["references", "inbox"],
    queryFn: () => api.get<{ requests: any[] }>("/references/inbox"),
  });
  if (isLoading) return <Spinner />;
  const pending = data?.requests.filter((r) => r.status === "REQUESTED") ?? [];

  return pending.length === 0 ? (
    <Alert>You have no outstanding reference requests.</Alert>
  ) : (
    <Alert tone="error">
      You have {pending.length} pending reference request{pending.length === 1 ? "" : "s"}.{" "}
      <Link className="underline" to="/references">Review them</Link>.
    </Alert>
  );
}

function CoordinatorHome() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => api.get<Record<string, number>>("/admin/overview"),
  });
  if (isLoading) return <Spinner />;

  const tiles: [string, number][] = [
    ["Students registered", data?.students ?? 0],
    ["EOIs submitted", data?.submitted ?? 0],
    ["Companies", data?.companies ?? 0],
    ["Companies awaiting approval", data?.pendingCompanies ?? 0],
    ["Positions", data?.positions ?? 0],
    ["Positions awaiting approval", data?.pendingPositions ?? 0],
    ["Applications", data?.applications ?? 0],
  ];

  const needsAttention = (data?.pendingCompanies ?? 0) + (data?.pendingPositions ?? 0);

  return (
    <>
      <PageHeading
        title="Coordinator dashboard"
        description="You are an academic staff member with coordinator access."
      />

      {/* A coordinator is still a referee, so their own inbox comes first. */}
      <RefereeSummary />

      {needsAttention > 0 && (
        <Alert tone="error">
          {needsAttention} item{needsAttention === 1 ? "" : "s"} awaiting your approval.{" "}
          <Link className="underline" to="/admin">Open the admin console</Link>.
        </Alert>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map(([label, value]) => (
          <div key={label} className="card">
            <p className="text-sm text-uoa-grey">{label}</p>
            <p className="mt-1 text-3xl font-semibold text-heading">{value}</p>
          </div>
        ))}
      </div>
    </>
  );
}
